import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import AssignmentIcon from '@mui/icons-material/Assignment';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import morningbreakfasticon from "../../../../assests/morning-breakfast-items.svg";
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import unlinkicon from "../../../../assests/unlinkicon.svg";
import AddIcon from '@mui/icons-material/Add';
import noRuleFound from "../../../..//assests/noRuleFound.svg";
import noRuleicon from "../../../..//assests/noruleicon.svg";
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import "./CohortAssortmentCreationComponent.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "../../../Loading/component";
import { Button } from "react-bootstrap-v5";
import brandicon from "../../../../assests/brandicon.svg";
import mvidicon from "../../../../assests/mvidicon.svg";
import offerpriceicon from "../../../../assests/offerpriceicon.svg";
import producticon from "../../../../assests/producticon.svg";
import varianticon from "../../../../assests/varianticon.svg";
import Assortment from "../../../../assests/assortment.svg";

const CohortAssortmentCreationComponent = (props) => {
  const navigate = useNavigate();
  const { reference_id } = useParams();
  const [userRole, setUserRole] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMainTab, setSelectedMainTab] = useState("active");
  const [showLinkSuccessModal, setShowLinkSuccessModal] = useState(false);
  const [linkedWarehouseDetails, setLinkedWarehouseDetails] = useState(null);
  const [showMachinesModal, setShowMachinesModal] = useState(false);
  const [machineSearchTerm, setMachineSearchTerm] = useState("");
  const [selectedHardwareType, setSelectedHardwareType] = useState(null);
  const [localWarehouseSearchTerm, setLocalWarehouseSearchTerm] = useState("");
  
  // State for assortment IDs popup
  const [showAssortmentIdsModal, setShowAssortmentIdsModal] = useState(false);
  const [selectedAssortmentIds, setSelectedAssortmentIds] = useState([]);
  const [selectedRuleInfo, setSelectedRuleInfo] = useState(null);
  
  // State for product names popup
  const [showProductNamesModal, setShowProductNamesModal] = useState(false);
  const [selectedProductNames, setSelectedProductNames] = useState([]);
  const [selectedProductRuleInfo, setSelectedProductRuleInfo] = useState(null);

  // Create refs for search inputs
  const machineSearchInputRef = useRef(null);
  const warehouseSearchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const warehouseSearchTimeoutRef = useRef(null);
  const isMounted = useRef(true);

  // Get warehouse metadata
  const warehouseMetadata = props.assortmentWarehouseMetaDataDetails || {};
  const warehouseName = warehouseMetadata.reference_name || "";
  const totalMachines = warehouseMetadata.total_machines || 0;
  const isLinked = warehouseMetadata.is_linked ;
  const linkedWarehouseName = warehouseMetadata.linked_warehouse_name || "";
  const activeAssortmentStartDate = warehouseMetadata.active_assortment_start_date || "";

  // Get assortment data
  const assortmentData = props.assortmentWarehouseMetaDataDetailsList || { active: [], scheduled: [], inactive: [] };
  const ruleData = props.assortmentWarehouseMetaDataRuleDetailsList || { active: [], scheduled: [], inactive: [] };
  const machinesData = props.assortmentCohortMachinesDetailsList || [];

  // Get current data based on selected tab
  const getCurrentAssortments = () => {
    switch (selectedMainTab) {
      case "active": return assortmentData.active || [];
      case "scheduled": return assortmentData.scheduled || [];
      case "inactive": return assortmentData.inactive || [];
      default: return [];
    }
  };

  const getCurrentRules = () => {
    switch (selectedMainTab) {
      case "active": return ruleData.active || [];
      case "scheduled": return ruleData.scheduled || [];
      case "inactive": return ruleData.inactive || [];
      default: return [];
    }
  };

  // Get icon based on rule type
  const getRuleTypeIcon = (ruleType) => {
    switch (ruleType) {
      case "MANUFACTURER_VARIANT":
        return <img src={mvidicon} className="cr-rule-type-icon" alt="MVID" />;
      case "OFFER_PRICE":
        return <img src={offerpriceicon} className="cr-rule-type-icon" alt="Offer Price" />;
      case "BRAND":
        return <img src={brandicon} className="cr-rule-type-icon" alt="Brand" />;
      case "VARIANT":
        return <img src={varianticon} className="cr-rule-type-icon" alt="Variant" />;
      case "PRODUCT":
        return <img src={producticon} className="cr-rule-type-icon" alt="Product" />;
      default:
        return <RuleFolderIcon className="cr-rule-type-icon" />;
    }
  };

  const currentAssortments = getCurrentAssortments();
  const currentRules = getCurrentRules();

  // Handle total mapped VM click
  const handleTotalMappedVMClick = useCallback(() => {
    setShowMachinesModal(true);
    if (machinesData && machinesData.length > 0) {
      setSelectedHardwareType(machinesData[0].hardwareTypeId);
    }
  }, [machinesData]);

  // Handle machine search
  const handleMachineSearchChange = useCallback((e) => {
    setMachineSearchTerm(e.target.value);
  }, []);

  const handleClearMachineSearch = useCallback((e) => {
    e.stopPropagation();
    setMachineSearchTerm("");
    setTimeout(() => {
      if (machineSearchInputRef.current) {
        machineSearchInputRef.current.focus();
      }
    }, 0);
  }, []);

  // Filter machines
  const getFilteredMachines = useCallback(() => {
    if (!machinesData) return [];
    if (selectedHardwareType) {
      const selectedHardware = machinesData.find(hw => hw.hardwareTypeId === selectedHardwareType);
      const machines = selectedHardware?.machineDetails || [];
      if (!machineSearchTerm.trim()) return machines;
      return machines.filter(machine =>
        machine.name?.toLowerCase().includes(machineSearchTerm.toLowerCase()) ||
        machine.id?.toString().includes(machineSearchTerm)
      );
    }
    const allMachines = machinesData.flatMap(hardwareType => hardwareType.machineDetails || []);
    if (!machineSearchTerm.trim()) return allMachines;
    return allMachines.filter(machine =>
      machine.name?.toLowerCase().includes(machineSearchTerm.toLowerCase()) ||
      machine.id?.toString().includes(machineSearchTerm)
    );
  }, [machinesData, selectedHardwareType, machineSearchTerm]);

  const filteredMachines = getFilteredMachines();

  // Handle warehouse search
  const handleWarehouseSearchChange = useCallback((e) => {
    const value = e.target.value;
    setLocalWarehouseSearchTerm(value);
    if (warehouseSearchTimeoutRef.current) {
      clearTimeout(warehouseSearchTimeoutRef.current);
    }
    warehouseSearchTimeoutRef.current = setTimeout(() => {
      if (props.onWarehouseSearch) {
        props.onWarehouseSearch(value);
      }
    }, 300);
  }, [props.onWarehouseSearch]);

  const handleWarehouseSearchKeyDown = useCallback((e) => e.stopPropagation(), []);
  const handleWarehouseSearchFocus = useCallback((e) => e.stopPropagation(), []);
  const handleWarehouseSearchBlur = useCallback((e) => e.stopPropagation(), []);
  const handleWarehouseSearchClick = useCallback((e) => e.stopPropagation(), []);

  // Handle link warehouse
  const handleLinkWarehouseSuccess = useCallback((warehouse) => {
    setLinkedWarehouseDetails({
      name: warehouse.warehouseName,
      id: warehouse.warehouseId,
      cohortName: warehouseName
    });
    setShowLinkSuccessModal(true);
  }, [warehouseName]);

  const handleLinkConfirm = useCallback(() => {
    if (props.onLinkWarehouseConfirm) {
      const selectedWarehouse = props.warehouseList.find(w => w.warehouseId === props.selectedWarehouse);
      props.onLinkWarehouseConfirm();
      if (selectedWarehouse) {
        handleLinkWarehouseSuccess(selectedWarehouse);
      }
    }
  }, [props, handleLinkWarehouseSuccess]);

  // Handle showing assortment IDs modal
  const handleShowAssortmentIds = (assortmentIds, ruleInfo) => {
    if (assortmentIds && assortmentIds.length > 0) {
      setSelectedAssortmentIds(assortmentIds);
      setSelectedRuleInfo(ruleInfo);
      setShowAssortmentIdsModal(true);
    }
  };

  // Handle showing product names modal
  const handleShowProductNames = (productNames, ruleInfo) => {
    if (productNames && productNames.length > 0) {
      setSelectedProductNames(productNames);
      setSelectedProductRuleInfo(ruleInfo);
      setShowProductNamesModal(true);
    }
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("data"));
    if (userData) {
      setUserRole(userData.details.userRole.role_name);
    }
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (warehouseSearchTimeoutRef.current) clearTimeout(warehouseSearchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setLocalWarehouseSearchTerm(props.warehouseSearchTerm || "");
  }, [props.warehouseSearchTerm]);

  const handleAssortmentDetails = useCallback((assortmentId) => {
    navigate(`/home/assortment/cohortassortmentdetails/${assortmentId}/${reference_id}`);
  }, [navigate, reference_id]);

  const handleCloneAssortment = useCallback((assortmentId) => {
    navigate(`/home/assortment/cohort/clone/${assortmentId}`);
  }, [navigate]);

  const handleCreateNewAssortment = useCallback(() => {
    navigate(`/home/assortment/newassortmentcohortcreation/${reference_id}`);
  }, [navigate, reference_id]);

  const handleCreateNewRule = useCallback(() => {
    navigate(`/home/assortment/newcohortassortmentrulecreation/${reference_id}`);
  }, [navigate, reference_id]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return `${date.getDate()} ${date.toLocaleDateString('en-US', { month: 'short' })} ${date.getFullYear()}`;
  }, []);

  const formatRuleType = useCallback((ruleType) => {
    switch (ruleType) {
      case "MANUFACTURER_VARIANT": return "MVID";
      case "OFFER_PRICE": return "Offer Price";
      case "BRAND": return "Brand";
      case "VARIANT": return "Variant";
      case "PRODUCT": return "Product";
      default: return ruleType || "";
    }
  }, []);

  const getRuleCondition = useCallback((rule) => {
    if (rule.condition === "restricted") return "Restricted";
    if (rule.condition === "quantity") return "Mandatory";
    return "Mandatory";
  }, []);

  // Parse assortment IDs
  const parseAssortmentIds = (assortmentIdsStr) => {
    if (!assortmentIdsStr) return [];
    return assortmentIdsStr.split(',').map(id => id.trim()).filter(id => id);
  };

  const renderAssortmentButtons = useCallback(() => {
  // If linked, don't show any buttons
  if (isLinked) return null;
  
  switch (selectedMainTab) {
    case "active":
      if (currentAssortments.length > 0) {
        return (
          <div className="cr-section-buttons">
            <Button 
              variant="outline-primary" 
              className="cr-schedule-button"
              onClick={() => handleCloneAssortment(currentAssortments[0].assortmentId)}
            >
              <ContentCopyIcon className="cr-button-icon" />
              Clone & Schedule
            </Button>
          </div>
        );
      }
      return null;
    case "scheduled":
      return (
        <div className="cr-section-buttons">
          <Button 
            variant="primary" 
            className="cr-create-button"
            onClick={handleCreateNewAssortment}
          >
            <AddIcon className="cr-button-icon" />
            Create New
          </Button>
        </div>
      );
    default: return null;
  }
}, [selectedMainTab, currentAssortments, handleCloneAssortment, handleCreateNewAssortment, isLinked]);

const renderRuleButtons = useCallback(() => {
  // If linked, don't show create rule button
  // if (isLinked) return null;
  
  if (selectedMainTab !== "inactive") {
    return (
      <div className="cr-section-buttons">
        <Button 
          variant="primary" 
          className="cr-rule-create-button"
          onClick={handleCreateNewRule}
        >
          <AddIcon className="cr-button-icon" />
          Create Rule
        </Button>
      </div>
    );
  }
  return null;
}, [selectedMainTab, handleCreateNewRule, isLinked]);
  const renderAssortmentContent = useCallback(() => {
    if (!currentAssortments || currentAssortments.length === 0) {
      return (
        <div className="cr-assortment-section">
          <div className="cr-section-header">
            <div className="cr-title-wrapper">
              <img src={Assortment} className="cr-section-icon" />
              <h2 className="cr-section-title">Assortment</h2>
            </div>
            {selectedMainTab === "scheduled" && renderAssortmentButtons()}
          </div>
          <div className="cr-empty-state">
            <img src={noRuleFound} className="cr-empty-icon" alt="No assortment" />
            <p>No Assortment Found</p>
            {selectedMainTab === "active" && (
              <>
                <p className="cr-empty-subtext">Create an assortment from the Schedule section.</p>
                <Link className="cr-switch-btn" onClick={() => setSelectedMainTab("scheduled")}>
                  Switch to Schedule
                </Link>
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="cr-assortment-section">
        <div className="cr-section-header">
          <div className="cr-title-wrapper">
            <img src={Assortment} className="cr-section-icon" />
            <h2 className="cr-section-title">Assortment</h2>
          </div>
          {renderAssortmentButtons()}
        </div>
        <div className="cr-assortment-list-container">
          {currentAssortments.map((item, index) => (
            <div key={item.assortmentId || index} className="cr-assortment-card">
              <div className="cr-assortment-left">
                <img src={morningbreakfasticon} alt="assortment" className="cr-assortment-img" />
                <span className="cr-item-badge">{item.itemCount || item.total_items || 0} Items</span>
              </div>
              <div className="cr-assortment-center">
                <div className="cr-created-info">
                  <span className="cr-created-label">Created by</span>
                  <span className="cr-created-value">{item.createdByName} (UserID: {item.createdBy})</span>
                </div>
                <div className="cr-assortment-row">
                  <div className="cr-assortment-col">
                    <span className="cr-label">Assortment Id:</span>
                    <span className="cr-value">{item.assortmentId || item.id || "N/A"}</span>
                  </div>
                  <div className="cr-assortment-col">
                    <span className="cr-separator">•</span>
                    <span className="cr-label">Start Date:</span>
                    <span className="cr-value">{formatDate(item.startDate)}</span>
                  </div>
                  {selectedMainTab === "inactive" && item.endDate && (
                    <>
                      <span className="cr-separator">•</span>
                      <div className="cr-assortment-col">
                        <span className="cr-label">End Date:</span>
                        <span className="cr-value">{formatDate(item.endDate)}</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="cr-status-row">
                  {item.ruleFailCount === 0 ? (
                    <>
                      <CheckCircleIcon className="cr-status-success" />
                      <span className="cr-status-text">All Rules Pass</span>
                    </>
                  ) : (
                    <>
                      <ErrorIcon className="cr-status-error" />
                      <span className="cr-status-text-error">
                        {item.ruleFailCount} Rule{item.ruleFailCount > 1 ? "s" : ""} Failed
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="cr-assortment-right">
                <button className="cr-view-button" onClick={() => handleAssortmentDetails(item.assortmentId || item.id)}>
                  <VisibilityIcon />
                </button>
                <span className="cr-view-text">View</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }, [currentAssortments, renderAssortmentButtons, handleAssortmentDetails, formatDate, selectedMainTab]);

  // Format product names with "see more" button
  const formatProductNames = useCallback((productNames, ruleInfo) => {
    if (!productNames || productNames.length === 0) return "N/A";
    if (productNames.length === 1) return productNames[0];
    if (productNames.length > 3) {
      return (
        <div className="cr-product-names-container">
          <span className="cr-product-names">{productNames.slice(0, 3).join(', ')}</span>
          <button 
            className="cr-product-see-more-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleShowProductNames(productNames, ruleInfo);
            }}
          >
            +{productNames.length - 3} more
          </button>
        </div>
      );
    }
    return productNames.join(', ');
  }, []);

  const renderRulesContent = useCallback(() => {
  if (!currentRules || currentRules.length === 0) {
    return (
      <div className="cr-rules-section">
        <div className="cr-section-header">
          <div className="cr-title-wrapper">
            <img src={noRuleicon} className="cr-section-main-icon" alt="No rules" />
            <h2 className="cr-section-title">Rules</h2>
          </div>
          {selectedMainTab !== "inactive" && renderRuleButtons()}
        </div>
        <div className="cr-empty-state">
          <img src={noRuleFound} className="cr-empty-icon" alt="No rules" />
          <p>No Rules Found</p>
        </div>
      </div>
    );
  }

  // Group rules by type and condition
  const groupedRules = currentRules.reduce((acc, rule) => {
    const key = `${rule.ruleType}-${rule.condition}`;
    if (!acc[key]) {
      acc[key] = {
        type: rule.ruleType,
        condition: rule.condition,
        createdBy: rule.createdByName || "Unknown",
        createdByName: rule.createdBy || "Unknown",
        products: []
      };
    }
    acc[key].products.push({
      name: rule.productNames || [],
      id: rule.ruleTypeIds || "N/A",
      ruleId: rule.ruleId,
      startDate: rule.startDate,
      endDate: rule.endDate,
      slot: rule.slot || "01",
      status: rule.status,
      value: rule.value,
      maxOfferPrice: rule.maxOfferPrice,
      ruleFailStatus: rule.ruleFailStatus,
      ruleType: rule.ruleType
    });
    return acc;
  }, {});

  // Get display value for Required Slot/Max Offer Price column
  const getSlotOrMaxPrice = (product) => {
    // For OFFER_PRICE rules, show maxOfferPrice
    if (product.ruleType === "OFFER_PRICE") {
      return product.maxOfferPrice ? `₹${product.maxOfferPrice}` : "N/A";
    }
    // For other rules, show value/slot
    return product.value || product.slot || "01";
  };

  return (
    <div className="cr-rules-section">
      <div className="cr-section-header">
        <div className="cr-title-wrapper">
          <img src={noRuleicon} className="cr-section-main-icon" alt="Rules" />
          <h2 className="cr-section-title">Rules</h2>
        </div>
        {selectedMainTab !== "inactive" && renderRuleButtons()}
      </div>
      <div className="cr-rules-list-container">
        {Object.values(groupedRules).map((ruleGroup, groupIndex) => {
          const ruleTypeIcon = getRuleTypeIcon(ruleGroup.type);
          const ruleTypeDisplay = formatRuleType(ruleGroup.type);
          const ruleCondition = ruleGroup.condition === "restricted" ? "Restricted" : "Mandatory";
          const conditionClass = ruleGroup.condition === "restricted" ? "cr-restricted" : "cr-mandatory";
          const isOfferPriceGroup = ruleGroup.type === "OFFER_PRICE";
          
          return (
            <div key={groupIndex} className="cr-rule-block">
              <div className="cr-rule-header">
                <div className="cr-rule-type-info">
                  <div className="cr-rule-type-wrapper">
                    <span className="cr-type-value">Type: </span>
                    <span className="cr-type-value">
                      {ruleTypeIcon} {ruleTypeDisplay}
                    </span>
                  </div>
                  <span className="cr-rule-creator">Created By {ruleGroup.createdBy} (UserID: {ruleGroup.createdByName})</span>
                </div>
                <div className={`cr-rule-condition ${conditionClass}`}>
                  {ruleCondition}
                </div>
              </div>
              <div className="cr-table-wrapper">
                <table className="cr-rules-table">
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>ID</th>
                      <th>Rule ID</th>
                      <th>Start Date</th>
                      {selectedMainTab === "inactive" && <th>End Date</th>}
                      <th>
                        {isOfferPriceGroup ? "Max Offer Price" : "Required Slot"}
                      </th>
                      <th>Status</th>
                      <th>Assortment IDs</th>
                      {selectedMainTab !== "inactive" && <th>Action</th>}
                      </tr>
                    </thead>
                  <tbody>
                    {ruleGroup.products.map((product, productIndex) => {
                      const hasRuleFailed = product.ruleFailStatus?.ruleFailStatus === true;
                      const assortmentIds = product.ruleFailStatus?.assortmentIds || "";
                      const assortmentIdList = parseAssortmentIds(assortmentIds);
                      const showSeeMore = assortmentIdList.length > 3;
                      const displayIds = showSeeMore ? assortmentIdList.slice(0, 3) : assortmentIdList;
                      const ruleInfo = {
                        ruleId: product.ruleId,
                        ruleName: Array.isArray(product.name) ? product.name.join(', ') : product.name,
                        ruleType: ruleGroup.type
                      };
                      return (
                        <tr key={productIndex}>
                          <td className="cr-product-cell">
                            {formatProductNames(product.name, ruleInfo)}
                          </td>
                          <td>
                            <span className="cr-id-badge">
                              {Array.isArray(product.id) ? product.id.join(', ') : product.id}
                            </span>
                          </td>
                          <td>
                            <span className="cr-rule-badge">{product.ruleId}</span>
                          </td>
                          <td>{formatDate(product.startDate)}</td>
                          {selectedMainTab === "inactive" && (
                            <td>{formatDate(product.endDate) || "N/A"}</td>
                          )}
                          <td>
                            <span className={isOfferPriceGroup ? "cr-max-price-badge" : "cr-slot-badge"}>
                              {getSlotOrMaxPrice(product)}
                            </span>
                          </td>
                          <td>
                            <span className={hasRuleFailed ? "cr-status-fail" : "cr-status-pass"}>
                              {hasRuleFailed ? "Fail" : "Pass"}
                            </span>
                          </td>
                          <td>
                            {assortmentIdList.length > 0 ? (
                              <div className="cr-assortment-ids-cell">
                                {displayIds.map((id, idx) => (
                                  <span key={idx} className="cr-assortment-id-badge">{id}</span>
                                ))}
                                {showSeeMore && (
                                  <button 
                                    className="cr-see-more-btn"
                                    onClick={() => handleShowAssortmentIds(assortmentIdList, ruleInfo)}
                                  >
                                    +{assortmentIdList.length - 3} more
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="cr-no-ids">-</span>
                            )}
                          </td>
                          {selectedMainTab !== "inactive" && (
                            <td>
                              <button
                                className="cr-delete-btn"
                                onClick={() => props.onAssortmentRuleRemove(product.ruleId)}
                                title="End Rule"
                               
                              >
                                <DeleteIcon className="cr-delete-icon" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}, [currentRules, renderRuleButtons, formatRuleType, getRuleCondition, formatDate, props, selectedMainTab, formatProductNames, parseAssortmentIds, getRuleTypeIcon, isLinked]);
  // Machines Modal
  const MachinesModal = useMemo(() => {
    if (!showMachinesModal) return null;
    return (
      <div className="cr-modal-overlay" onClick={() => setShowMachinesModal(false)}>
        <div className="cr-modal-container cr-machines-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cr-modal-header" style={{display:"flex", flexDirection:"row"}}>
            <h3>Total Mapped VM ({totalMachines})</h3>
            <button className="cr-modal-close" onClick={() => setShowMachinesModal(false)}>
              <CloseIcon />
            </button>
          </div>
          <div className="cr-modal-body">
            {machinesData && machinesData.length > 0 && (
              <div className="cr-hardware-tabs">
                {machinesData.map((hardwareType) => (
                  <div
                    key={hardwareType.hardwareTypeId}
                    className={`cr-hardware-tab ${selectedHardwareType === hardwareType.hardwareTypeId ? "active" : ""}`}
                    onClick={() => {
                      setSelectedHardwareType(hardwareType.hardwareTypeId);
                      setMachineSearchTerm("");
                    }}
                  >
                    <span className="cr-hardware-name">{hardwareType.hardwareTypeName}</span>
                    <span className="cr-hardware-count">{hardwareType.machineDetails?.length || 0}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="cr-search-box" onClick={(e) => e.stopPropagation()}>
              <SearchIcon className="cr-search-icon" />
              <input
                ref={machineSearchInputRef}
                type="text"
                placeholder="Search by name or ID..."
                value={machineSearchTerm}
                onChange={handleMachineSearchChange}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                className="cr-search-input"
                autoComplete="off"
              />
              {machineSearchTerm && machineSearchTerm.trim() !== "" && (
                <button className="cr-clear-search-btn" onClick={handleClearMachineSearch} type="button">
                  ✕
                </button>
              )}
            </div>
            <div className="cr-table-container">
              <table className="cr-vm-table">
                <thead>
                  <tr>
                    <th className="cr-number-col">#</th>
                    <th className="cr-name-col">Machine Name</th>
                    <th className="cr-id-col">Machine ID</th>
                    </tr>
                   </thead>
                <tbody>
                  {filteredMachines.length > 0 ? (
                    filteredMachines.map((machine, index) => (
                      <tr key={machine.id || index} className="cr-table-row">
                        <td className="cr-number">{index + 1}</td>
                        <td className="cr-name">{machine.name || "N/A"}</td>
                        <td className="cr-id">{machine.id || "N/A"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="cr-empty-cell">
                        <PrecisionManufacturingIcon className="cr-empty-icon" />
                        <p>{machineSearchTerm ? `No machines found matching "${machineSearchTerm}"` : "No machines found"}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }, [showMachinesModal, totalMachines, machinesData, selectedHardwareType, machineSearchTerm, filteredMachines, handleMachineSearchChange, handleClearMachineSearch]);

  // Assortment IDs Modal
  const AssortmentIdsModal = useMemo(() => {
    if (!showAssortmentIdsModal) return null;
    return (
      <div className="cr-modal-overlay" onClick={() => setShowAssortmentIdsModal(false)}>
        <div className="cr-modal-container cr-assortment-ids-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cr-modal-header">
            <h3>Assortment IDs</h3>
            <button className="cr-modal-close" onClick={() => setShowAssortmentIdsModal(false)}>
              <CloseIcon />
            </button>
          </div>
          <div className="cr-modal-body">
            {selectedRuleInfo && (
              <div className="cr-rule-info-summary">
                <p><strong>Rule ID:</strong> {selectedRuleInfo.ruleId}</p>
                <p><strong>Rule Name:</strong> {selectedRuleInfo.ruleName || "N/A"}</p>
                <p><strong>Rule Type:</strong> {formatRuleType(selectedRuleInfo.ruleType)}</p>
              </div>
            )}
            <div className="cr-assortment-ids-grid">
              {selectedAssortmentIds.map((id, index) => (
                <div key={index} className="cr-assortment-id-item">
                  <span className="cr-assortment-id-number">{index + 1}.</span>
                  <span className="cr-assortment-id-value">{id}</span>
                  <button 
                    className="cr-view-assortment-btn"
                    onClick={() => handleAssortmentDetails(id)}
                    title="View Assortment Details"
                  >
                    <VisibilityIcon />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="cr-modal-footer">
            <button className="cr-ok-button" onClick={() => setShowAssortmentIdsModal(false)}>Close</button>
          </div>
        </div>
      </div>
    );
  }, [showAssortmentIdsModal, selectedAssortmentIds, selectedRuleInfo, formatRuleType, handleAssortmentDetails]);

  // Product Names Modal
  const ProductNamesModal = useMemo(() => {
    if (!showProductNamesModal) return null;
    return (
      <div className="cr-modal-overlay" onClick={() => setShowProductNamesModal(false)}>
        <div className="cr-modal-container cr-product-names-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cr-modal-header" style={{textAlign:"center"}}>
            <h3>Product Names</h3>
          </div>
          <div className="cr-modal-body">
            <div className="cr-product-names-grid">
              {selectedProductNames.map((name, index) => (
                <div key={index} className="cr-product-name-item">
                  <span className="cr-product-name-number">{index + 1}.</span>
                  <span className="cr-product-name-value">{name}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="cr-modal-footer">
            <button className="cr-ok-button" onClick={() => setShowProductNamesModal(false)}>Close</button>
          </div>
        </div>
      </div>
    );
  }, [showProductNamesModal, selectedProductNames]);

  // End Rule Confirmation Modal
  const EndRuleConfirmationModal = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    if (!props.showEndRuleModal) return null;
    return (
      <div className="cr-modal-overlay" onClick={props.onCloseEndRuleModal}>
        <div className="cr-modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="cr-modal-header">
            <h3>Are You Sure You Want To End This Rule?</h3>
          </div>
          <div className="cr-modal-body">
            <div className="cr-field-group">
              <label>Select End Date</label>
              <input
                type="date"
                value={props.endDate}
                onChange={(e) => props.onEndDateChange(e.target.value)}
                className="cr-date-field"
                min={today}
              />
            </div>
          </div>
          <div className="cr-modal-footer">
            <button className="cr-cancel-button" onClick={props.onCloseEndRuleModal}>Cancel</button>
            <button className="cr-confirm-button" onClick={props.onEndRuleConfirm} disabled={!props.endDate}>End Rule</button>
          </div>
        </div>
      </div>
    );
  }, [props.showEndRuleModal, props.endDate, props.onCloseEndRuleModal, props.onEndDateChange, props.onEndRuleConfirm]);

  // Link Warehouse Modal
  const LinkWarehouseModal = useMemo(() => {
    if (!props.showLinkWarehouseModal) return null;
    
    // Check if there's an active assortment
    const hasActiveAssortment = currentAssortments && currentAssortments.length > 0 && 
      currentAssortments.some(assortment => 
        assortment.status === "ACTIVE" || 
        (selectedMainTab === "active" && assortment.startDate <= new Date().toISOString())
      );

    return (
      <div className="cr-modal-overlay">
        <div className="cr-modal-container cr-warehouse-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cr-modal-header-link">
            <h3>Link Warehouse Assortment With This Cohort</h3>
            <button className="cr-modal-close" onClick={props.onCloseLinkWarehouseModal}>
              <CloseIcon />
            </button>
          </div>
          <div className="cr-modal-body">
            {/* Warning message only shown when there's an active assortment */}
            {hasActiveAssortment && (
              <div className="cr-warning-message">
                <div className="cr-warning-icon">
                  <ErrorIcon />
                </div>
                <div className="cr-warning-text">
                  <strong >Warning: This cohort currently uses an Independent Assortment. </strong> 
                  Linking a warehouse will switch it to the warehouse's assortment.
                </div>
              </div>
            )}
            <div className="cr-search-box cr-modal-search" onClick={(e) => e.stopPropagation()}>
              <SearchIcon className="cr-search-icon" />
              <input
                ref={warehouseSearchInputRef}
                type="text"
                placeholder="Search and select warehouse"
                value={localWarehouseSearchTerm}
                onChange={handleWarehouseSearchChange}
                onKeyDown={handleWarehouseSearchKeyDown}
                onFocus={handleWarehouseSearchFocus}
                onBlur={handleWarehouseSearchBlur}
                onClick={handleWarehouseSearchClick}
                className="cr-search-input"
                autoComplete="off"
              />
            </div>
            <div className="cr-warehouse-list" onClick={(e) => e.stopPropagation()}>
              {props.warehouseList.length > 0 ? (
                props.warehouseList.map((warehouse) => (
                  <label key={warehouse.warehouseId} className="cr-warehouse-item" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={props.selectedWarehouse === warehouse.warehouseId}
                      onChange={() => props.setSelectedWarehouse(warehouse.warehouseId)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="cr-warehouse-name">{warehouse.warehouseName}</span>
                  </label>
                ))
              ) : (
                <p className="cr-no-results" onClick={(e) => e.stopPropagation()}>
                  {localWarehouseSearchTerm ? `No warehouses found matching "${localWarehouseSearchTerm}"` : "No warehouses found"}
                </p>
              )}
            </div>
          </div>
          <div className="cr-modal-footer" onClick={(e) => e.stopPropagation()}>
            <button className="cr-cancel-button" onClick={props.onCloseLinkWarehouseModal}>Cancel</button>
            <button className="cr-confirm-button cr-link-confirm" onClick={handleLinkConfirm} disabled={!props.selectedWarehouse}>Link</button>
          </div>
        </div>
      </div>
    );
  }, [props.showLinkWarehouseModal, props.warehouseList, props.selectedWarehouse, props.onCloseLinkWarehouseModal, props.setSelectedWarehouse, localWarehouseSearchTerm, handleWarehouseSearchChange, handleWarehouseSearchKeyDown, handleWarehouseSearchFocus, handleWarehouseSearchBlur, handleWarehouseSearchClick, handleLinkConfirm, currentAssortments, selectedMainTab]);

  // Link Success Modal
  const LinkSuccessModal = useMemo(() => {
    if (!showLinkSuccessModal || !linkedWarehouseDetails) return null;
    return (
      <div className="cr-modal-overlay" onClick={() => setShowLinkSuccessModal(false)}>
        <div className="cr-modal-container cr-link-success-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cr-modal-body">
            <div className="cr-link-success-content">
              <div className="cr-link-success-icon"><CheckCircleOutlineIcon /></div>
              <h4 className="cr-link-success-message">Warehouse Linked Successfully!</h4>
              <p className="cr-link-success-info">The <strong>{linkedWarehouseDetails.name}</strong> base assortment has been applied to this cohort.</p>
            </div>
          </div>
          <div className="cr-modal-footer">
            <button className="cr-ok-button" onClick={() => setShowLinkSuccessModal(false)}>Ok</button>
          </div>
        </div>
      </div>
    );
  }, [showLinkSuccessModal, linkedWarehouseDetails]);

  // Unlink Confirmation Modal
  const UnlinkConfirmationModal = useMemo(() => {
    if (!props.showUnlinkConfirmationModal) return null;
    return (
      <div className="cr-modal-overlay" onClick={props.onCloseUnlinkConfirmationModal}>
        <div className="cr-modal-container cr-unlink-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cr-modal-header">
            <img src={unlinkicon} alt="Unlink" />
            <h4>Unlink Assortment ?</h4>
          </div>
          <div className="cr-modal-body">
            <div className="cr-unlink-content">
              <p className="cr-unlink-message">Unlinking will remove the Warehouse Assortment from this cohort.</p>
            </div>
          </div>
          <div className="cr-modal-footer">
            <button className="cr-cancel-button" onClick={props.onCloseUnlinkConfirmationModal}>No</button>
            <button className="cr-confirm-button cr-unlink-confirm" onClick={props.onUnlinkConfirm}>Yes</button>
          </div>
        </div>
      </div>
    );
  }, [props.showUnlinkConfirmationModal, props.onCloseUnlinkConfirmationModal, props.onUnlinkConfirm]);

  // Unlink Success Modal
  const UnlinkSuccessModal = useMemo(() => {
    if (!props.showUnlinkSuccessModal) return null;
    return (
      <div className="cr-modal-overlay" onClick={props.onUnlinkSuccessOk}>
        <div className="cr-modal-container cr-success-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cr-modal-header">
            <div className="cr-link-success-icon"><CheckCircleOutlineIcon /></div>
            <h5>Unlinked Successfully</h5>
          </div>
          <div className="cr-modal-body">
            <div className="cr-success-content">
              <p className="cr-success-message">The warehouse assortment has been removed from this cohort.</p>
            </div>
          </div>
          <div className="cr-modal-footer">
            <button className="cr-ok-button" onClick={props.onUnlinkSuccessOk}>Ok</button>
          </div>
        </div>
      </div>
    );
  }, [props.showUnlinkSuccessModal, props.onUnlinkSuccessOk]);

  return (
    <>
      {props.loading ? (
        <LoadingSpinner />
      ) : (
        <div className="cr-main-layout">
          <Sidebar />
          <div className="cr-content-area">
            <div className="cr-page-header">
              <button className="cr-back-button" onClick={() => navigate(-1)}>←</button>
              <div className="cr-header-info">
                <h2 className="cr-page-title">{warehouseName} {isLinked && linkedWarehouseName && <span>Linked Warehouse {linkedWarehouseName}</span>}</h2>
                <div className="cr-stat-badges">
                  <div className="cr-stat-item cr-clickable-stat" onClick={handleTotalMappedVMClick}>
                    <span>Total Mapped VM - {totalMachines}</span>
                  </div>
                </div>
              </div>
              <div className="cr-header-actions">
                {isLinked ? (
                  <button className="cr-unlink-button" onClick={props.onUnlinkWarehouse}>
                    <LinkOffIcon className="cr-button-icon" />
                    <span>Unlink Warehouse</span>
                  </button>
                ) : (
                  <button className="cr-link-button" onClick={props.onLinkWarehouse}>
                    <LinkIcon className="cr-button-icon" />
                    <span>Link Warehouse</span>
                  </button>
                )}
              </div>
            </div>
            <div className="cr-main-tabs">
              <button className={`cr-main-tab tab-active ${selectedMainTab === "active" ? "active-tab-dark" : ""}`} onClick={() => setSelectedMainTab("active")}>Active</button>
              <button className={`cr-main-tab tab-schedule ${selectedMainTab === "scheduled" ? "active-tab-dark" : ""}`} onClick={() => setSelectedMainTab("scheduled")}>Schedule</button>
              <button className={`cr-main-tab tab-inactive ${selectedMainTab === "inactive" ? "active-tab-dark" : ""}`} onClick={() => setSelectedMainTab("inactive")}>Inactive</button>
            </div>
            <div className="cr-sections-container">
              {renderAssortmentContent()}
              {renderRulesContent()}
            </div>
          </div>
        </div>
      )}
      {MachinesModal}
      {AssortmentIdsModal}
      {ProductNamesModal}
      {EndRuleConfirmationModal}
      {LinkWarehouseModal}
      {LinkSuccessModal}
      {UnlinkConfirmationModal}
      {UnlinkSuccessModal}
    </>
  );
};

export default CohortAssortmentCreationComponent;